// ABOUTME: Sanitization utilities for KB article HTML content, policy markdown rendering, and URL validation.
// ABOUTME: Used by ArticleDetailPage, CampaignStepsTab, and all policy display components to prevent stored XSS.

import DOMPurify from 'dompurify';
import { marked } from 'marked';

const KB_ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's',
  'ul', 'ol', 'li',
  'a', 'code', 'pre', 'blockquote', 'hr',
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'img', 'figure', 'figcaption', 'picture', 'source', 'video', 'audio',
  'div', 'span', 'section', 'article', 'header', 'footer', 'aside', 'nav',
  'iframe',
];

const KB_ALLOWED_ATTR = [
  'href', 'title', 'src', 'alt', 'width', 'height', 'style', 'class', 'id',
  'target', 'rel', 'controls', 'autoplay', 'muted', 'loop', 'preload',
  'type', 'colspan', 'rowspan', 'scope', 'headers',
  'allowfullscreen', 'allow', 'frameborder', 'loading', 'referrerpolicy',
];

export function sanitizeKBHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: KB_ALLOWED_TAGS,
    ALLOWED_ATTR: KB_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    // FORCE_BODY ensures content is parsed in body context, preventing namespace tricks
    // where attackers inject head/html tags to escape the fragment context.
    FORCE_BODY: true,
  });
}

const POLICY_ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'strong', 'em',
  'ul', 'ol', 'li',
  'a', 'code', 'pre', 'blockquote',
];

const POLICY_ALLOWED_ATTR = ['href', 'title'];

export function renderPolicyMarkdown(content: string): string {
  const trimmed = content.split('\n').map(line => line.trimStart()).join('\n');
  const html = marked.parse(trimmed, { async: false }) as string;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: POLICY_ALLOWED_TAGS,
    ALLOWED_ATTR: POLICY_ALLOWED_ATTR,
    FORCE_BODY: true,
  });
}

export function isValidYouTubeUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return (
    url.startsWith('https://www.youtube.com/') ||
    url.startsWith('https://youtube.com/') ||
    url.startsWith('https://youtu.be/')
  );
}

export function isValidVimeoUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.startsWith('https://vimeo.com/');
}

export function isValidS3Url(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.startsWith('https://');
}
